import React, { createContext, PropsWithChildren, useContext, useReducer } from 'react';
import { FactureFormalite, Devis } from '@europrocurement/l2d-domain';
import { CollectionOpenSearchJsonldOpensearchCollectionRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from '../modules/Dossiers/hooks/useDossierPrefacturation';
import { CollectionProvisionType } from '../modules/Dossiers/hooks/useCollectionProvision';
import { CollectionReglementType } from '../modules/Dossiers/hooks/useCollectionReglement';
import { DataProviderProps, DataState } from './types';

// ---- Collection ----

export enum TypeFacturation {
    COMPLETE = 'COMPLETE',
    SIMPLIFIEE = 'SIMPLIFIEE',
    AGREGEE = 'AGREGEE',
}

export type Collection = {
    id: number;
    numero: string;
    venteIndirecte: boolean;
    prescripteurId: number;
    clientOpereId: number;
    clientId: number;
    typeFacturation: TypeFacturation | null;
    acompteAttendu: number | null;
    dossierIds: number[];
};

export const collectionApiToStateTransformer = (
    collectionOpenSearch: CollectionOpenSearchJsonldOpensearchCollectionRead,
): Collection => ({
    id: collectionOpenSearch.id as number,
    numero: collectionOpenSearch.numero as string,
    venteIndirecte: collectionOpenSearch.venteIndirecte as boolean,
    prescripteurId: collectionOpenSearch.prescripteurId as number,
    clientOpereId: (collectionOpenSearch.dossiers || [])[0]?.clientOpereId as number,
    clientId: collectionOpenSearch.clientId as number,
    typeFacturation:
        (collectionOpenSearch.typeFacturation as TypeFacturation | null | undefined) ?? null,
    acompteAttendu:
        collectionOpenSearch.acompteAttendu != null
            ? Number(collectionOpenSearch.acompteAttendu)
            : null,
    dossierIds: (collectionOpenSearch.dossiers ?? []).map((d) => d.id as number),
});

export const ADD_COLLECTION_CTX = 'add_collection_ctx';
export const UPDATE_COLLECTION_CTX = 'update_collection_ctx';
export const REMOVE_COLLECTION_CTX = 'remove_collection_ctx';
export const INIT_COLLECTION_CTX = 'init_collection_ctx';

// ---- CollectionInvoice ----

export type CollectionInvoice = {
    id: number;
    createdBy: string;
    createdAt: string;
    totalTtc: number;
    factureEnPreparation: boolean;
    idFactureRefAvoir: number;
    numero: string;
    paye: boolean;
    montantPaiementRecu: number;
    factureAvoir: boolean;
    deleted: boolean;
};

export const collectionInvoiceApiToStateTransformer = (
    invoice: FactureFormalite,
): CollectionInvoice => ({
    id: invoice.id as number,
    createdBy: invoice.createdBy as string,
    createdAt: invoice.createdAt as string,
    totalTtc: Number(invoice.totalTtc),
    factureEnPreparation: invoice.factureEnPreparation as boolean,
    idFactureRefAvoir: invoice.idFactureRefAvoir as number,
    numero: invoice.numero as string,
    paye: invoice.paye as boolean,
    montantPaiementRecu: Number(invoice.montantPaiementRecu),
    factureAvoir: invoice.factureAvoir as boolean,
    deleted: invoice.deleted as boolean,
});

export const INIT_COLLECTION_INVOICE_CTX = 'init_collection_invoice_ctx';
export const START_LOAD_COLLECTION_INVOICE_CTX = 'start_load_collection_invoice_ctx';
export const STOP_LOAD_COLLECTION_INVOICE_CTX = 'stop_load_collection_invoice_ctx';
export const START_FETCH_COLLECTION_INVOICE_CTX = 'start_fetch_collection_invoice_ctx';
export const STOP_FETCH_COLLECTION_INVOICE_CTX = 'stop_fetch_collection_invoice_ctx';
export const REMOVE_COLLECTION_INVOICE_CTX = 'remove_collection_invoice_ctx';
export const UPDATE_COLLECTION_INVOICE_CTX = 'update_collection_invoice_ctx';
export const ADD_COLLECTION_INVOICE_CTX = 'add_collection_invoice_ctx';

// ---- CollectionEstimate ----

export type CollectionEstimate = {
    id: number;
    numero: string;
    active: boolean;
};

export const collectionEstimateApiToStateTransformer = (estimate: Devis): CollectionEstimate => ({
    id: estimate.id as number,
    numero: estimate.numero as string,
    active: estimate.actif as boolean,
});

export const INIT_COLLECTION_ESTIMATE_CTX = 'init_collection_estimate_ctx';
export const START_LOAD_COLLECTION_ESTIMATE_CTX = 'start_load_collection_estimate_ctx';
export const STOP_LOAD_COLLECTION_ESTIMATE_CTX = 'stop_load_collection_estimate_ctx';
export const START_FETCH_COLLECTION_ESTIMATE_CTX = 'start_fetch_collection_estimate_ctx';
export const STOP_FETCH_COLLECTION_ESTIMATE_CTX = 'stop_fetch_collection_estimate_ctx';

export const SET_RECIPIENT_ID_CTX = 'set_recipient_id_ctx';
export const SET_DATA_PREFACTURATION_CTX = 'set_data_prefacturation_ctx';
export const START_LOAD_DATA_PREFACTURATION_CTX = 'start_load_data_prefacturation_ctx';
export const STOP_LOAD_DATA_PREFACTURATION_CTX = 'stop_load_data_prefacturation_ctx';
export const START_FETCH_DATA_PREFACTURATION_CTX = 'start_fetch_data_prefacturation_ctx';
export const STOP_FETCH_DATA_PREFACTURATION_CTX = 'stop_fetch_data_prefacturation_ctx';
export const UPDATE_ARTICLE_DATA_PREFACTURATION_CTX = 'update_article_data_prefacturation_ctx';
export const ADD_ARTICLE_DATA_PREFACTURATION_CTX = 'add_article_data_prefacturation_ctx';
export const REMOVE_ARTICLE_DATA_PREFACTURATION_CTX = 'remove_article_data_prefacturation_ctx';
export const SET_BILLING_DOSSIER_IDS_CTX = 'set_billing_dossier_ids_ctx';
export const START_LOAD_BILLING_DOSSIER_IDS_CTX = 'start_load_billing_dossier_ids_ctx';
export const STOP_LOAD_BILLING_DOSSIER_IDS_CTX = 'stop_load_billing_dossier_ids_ctx';
export const START_FETCH_BILLING_DOSSIER_IDS_CTX = 'start_fetch_billing_dossier_ids_ctx';
export const STOP_FETCH_BILLING_DOSSIER_IDS_CTX = 'stop_fetch_billing_dossier_ids_ctx';
export const SET_ACOMPTES_CTX = 'set_acomptes_ctx';
export const ADD_ACOMPTE_CTX = 'add_acompte_ctx';
export const UPDATE_ACOMPTE_CTX = 'update_acompte_ctx';
export const REMOVE_ACOMPTE_CTX = 'remove_acompte_ctx';
export const START_FETCH_ACOMPTES_CTX = 'start_fetch_acomptes_ctx';
export const STOP_FETCH_ACOMPTES_CTX = 'stop_fetch_acomptes_ctx';
export const SET_REGLEMENTS_CTX = 'set_reglements_ctx';
export const UPDATE_REGLEMENT_CTX = 'update_reglement_ctx';
export const REMOVE_REGLEMENT_CTX = 'remove_reglement_ctx';
export const START_FETCH_REGLEMENTS_CTX = 'start_fetch_reglements_ctx';
export const STOP_FETCH_REGLEMENTS_CTX = 'stop_fetch_reglements_ctx';

// ---- Acompte ----
export type Acompte = {
    id: number;
    isAcompte: boolean;
    mode: 'CH' | 'ESP' | 'VIR';
    montant: number;
    transactioncb?: string;
    reference?: string;
    origine: number;
    detailsReglements: Array<{
        id: number;
        idCollection: number;
        ttc: number;
    }>;
    note?: string;
    dreception?: Date;
    usercreation?: number;
};

export const collectionProvisionToAcompte = (provision: CollectionProvisionType): Acompte => ({
    id: provision.id as number,
    isAcompte: true,
    mode: provision.mode as Acompte['mode'],
    montant: Number(provision.montant),
    transactioncb: provision.transactioncb ?? undefined,
    reference: provision.reference ?? undefined,
    origine: provision.origine as number,
    detailsReglements: (provision.detailsReglements ?? []).map((detail) => ({
        id: Number(detail.id),
        idCollection: Number(detail.idCollection),
        ttc: Number(detail.ttc),
    })),
    note: provision.note ?? undefined,
    dreception: provision?.dreception ? new Date(provision.dreception) : undefined,
    usercreation: provision.usercreation ?? undefined,
});

// ---- Reglement ----

export type Reglement = {
    id: number;
    mode: string;
    montant: number;
    transactioncb?: string;
    reference?: string;
    origine?: number;
    detailsReglements: Array<{
        idCollection: number;
        ttc: number;
        facture?: {
            id?: number;
            numero?: string;
            factureEnPreparation?: boolean;
        } | null;
    }>;
    note?: string;
    dreception?: Date;
    usercreation?: number;
};

export const collectionReglementToReglement = (reglement: CollectionReglementType): Reglement => ({
    id: reglement.id as number,
    mode: reglement.mode as string,
    montant: Number(reglement.montant),
    transactioncb: reglement.transactioncb ?? undefined,
    reference: reglement.reference ?? undefined,
    origine: reglement.origine ?? undefined,
    detailsReglements: (reglement.detailsReglements ?? []).map((detail) => ({
        idCollection: Number(detail.idCollection),
        ttc: Number(detail.ttc),
        facture: detail.facture
            ? {
                  id: detail.facture.id,
                  numero: detail.facture.numero ?? undefined,
                  factureEnPreparation: detail.facture.factureEnPreparation ?? undefined,
              }
            : null,
    })),
    note: reglement.note ?? undefined,
    dreception: reglement.dreception ? new Date(reglement.dreception) : undefined,
    usercreation: reglement.usercreation ?? undefined,
});

// ---- DataPrefacturation ----

export type ArticlePrefacturation = {
    id: number;
    rubFac: string;
    prixBase: string;
    articleFacturable: boolean;
    fMasqueCalcul: boolean;
    fAjouteCalcul: boolean;
    libelle: string;
    articleAjustement: boolean;
    quantite: number;
    tauxTva: string;
    ttc: number;
    articleModifiable: boolean;
    articleFacture: boolean;
    articleFactureValidee: boolean;
    articleFactureNumero: string;
    articleSupprimable: boolean;
    motifNonFacturable: string;
    idAnnonce?: string;
    prestationId: number;
    rang: number;
};

export type DossierPrefacturation = {
    id: number;
    prestations: Array<{
        id: number;
        libelle: string;
    }>;
    dossierFacturable: boolean;
    dossierFacture: boolean;
    motifNonFacturable: string;
    numero: string;
    ttc: string;
    resteAFacturer: string;
    articles: Array<ArticlePrefacturation>;
    reductions: Array<ArticlePrefacturation>;
};

export type DataPrefacturation = {
    dossiers: Array<DossierPrefacturation>;
    resteAFacturer: string;
    ttcProforma: string;
    ttc: string;
    facturable: boolean;
    motifNonFacturable: string;
    tauxTvas: Array<{
        codeTva: string;
        tauxTva: string;
    }>;
};

const articleApiToArticlePrefacturation = (
    article: NonNullable<
        NonNullable<DossiersArticlesReductions['dossiers']>[number]['articles']
    >[number],
): ArticlePrefacturation => ({
    id: article.id as number,
    rubFac: article.rubFac as string,
    prixBase: article.prixBase as string,
    articleFacturable: article.articleFacturable as boolean,
    fMasqueCalcul: article.fMasqueCalcul as boolean,
    fAjouteCalcul: article.fAjouteCalcul as boolean,
    libelle: article.libelle as string,
    articleAjustement: article.articleAjustement as boolean,
    quantite: article.quantite as number,
    tauxTva: article.tauxTva as string,
    ttc: Number(article.ttc),
    articleModifiable: article.articleModifiable as boolean,
    articleFacture: article.articleFacture as boolean,
    articleFactureValidee: article.articleFactureValidee as boolean,
    articleFactureNumero: article.articleFactureNumero as string,
    articleSupprimable: article.articleSupprimable as boolean,
    motifNonFacturable: article.motifNonFacturable as string,
    idAnnonce: article.idAnnonce as string,
    prestationId: article.prestationId as number,
    rang: article.rang as number,
});

export const dataPrefacturationApiToStateTransformer = (
    rawData: DossiersArticlesReductions,
): DataPrefacturation => ({
    dossiers: (rawData.dossiers ?? []).map(
        (dossier) =>
            ({
                id: dossier.id as number,
                prestations: (dossier.prestations ?? []).map((p) => ({
                    id: p.id as number,
                    libelle: p.libelle as string,
                })),
                dossierFacturable: dossier.dossierFacturable as boolean,
                dossierFacture: dossier.dossierFacture as boolean,
                motifNonFacturable: dossier.motifNonFacturable as string,
                numero: dossier.numero as string,
                ttc: dossier.ttc as string,
                resteAFacturer: dossier.resteAFacturer as string,
                articles: (dossier.articles ?? []).map(articleApiToArticlePrefacturation),
                reductions: (dossier.reductions ?? []).map((reduction) => ({
                    id: 0,
                    rubFac: reduction.rubFac as string,
                    prixBase: reduction.prixBase as string,
                    articleFacturable: false,
                    fMasqueCalcul: false,
                    libelle: reduction.libelle as string,
                    articleAjustement: false,
                    quantite: reduction.quantite as number,
                    tauxTva: reduction.tauxTva as string,
                    ttc: Number(reduction.ttc),
                    articleModifiable: false,
                    articleFacture: false,
                    articleFactureValidee: false,
                    articleFactureNumero: '',
                    articleSupprimable: false,
                })),
            }) as DossierPrefacturation,
    ),
    resteAFacturer: rawData.resteAFacturer as string,
    ttcProforma: rawData.ttcProforma as string,
    ttc: rawData.ttc as string,
    facturable: rawData.facturable as boolean,
    motifNonFacturable: rawData.motifNonFacturable as string,
    tauxTvas: (rawData.tauxTvas ?? []).map((tva) => ({
        codeTva: tva.codeTva as string,
        tauxTva: tva.tauxTva as string,
    })),
});

// ---- State ----

type CollectionInvoiceState = DataState & {
    fetching: boolean;
    data: Array<CollectionInvoice>;
};

type CollectionEstimateState = DataState & {
    fetching: boolean;
    data: Array<CollectionEstimate>;
};

type CollectionState = DataState & {
    data: Array<Collection>;
};

type DataPrefacturationState = DataState & {
    fetching: boolean;
    data: DataPrefacturation | null;
};

type BillingDossierIdsState = DataState & {
    fetching: boolean;
    data: number[];
};

type AcomptesState = DataState & {
    data: Array<Acompte>;
};

type ReglementsState = DataState & {
    data: Array<Reglement>;
};

type CollectionBillingState = {
    invoices: CollectionInvoiceState;
    estimates: CollectionEstimateState;
    collections: CollectionState;
    recipientId: number | null;
    dataPrefacturation: DataPrefacturationState;
    billingDossierIds: BillingDossierIdsState;
    acomptes: AcomptesState;
    reglements: ReglementsState;
};

// ---- Actions ----

type Action =
    | { type: typeof INIT_COLLECTION_INVOICE_CTX; payload: CollectionInvoice[] }
    | { type: typeof START_LOAD_COLLECTION_INVOICE_CTX }
    | { type: typeof STOP_LOAD_COLLECTION_INVOICE_CTX }
    | { type: typeof START_FETCH_COLLECTION_INVOICE_CTX }
    | { type: typeof STOP_FETCH_COLLECTION_INVOICE_CTX }
    | { type: typeof REMOVE_COLLECTION_INVOICE_CTX; payload: number }
    | { type: typeof UPDATE_COLLECTION_INVOICE_CTX; payload: CollectionInvoice }
    | { type: typeof ADD_COLLECTION_INVOICE_CTX; payload: CollectionInvoice }
    | { type: typeof INIT_COLLECTION_ESTIMATE_CTX; payload: CollectionEstimate[] }
    | { type: typeof START_LOAD_COLLECTION_ESTIMATE_CTX }
    | { type: typeof STOP_LOAD_COLLECTION_ESTIMATE_CTX }
    | { type: typeof START_FETCH_COLLECTION_ESTIMATE_CTX }
    | { type: typeof STOP_FETCH_COLLECTION_ESTIMATE_CTX }
    | { type: typeof SET_RECIPIENT_ID_CTX; payload: number | null }
    | { type: typeof SET_DATA_PREFACTURATION_CTX; payload: DataPrefacturation | null }
    | { type: typeof START_LOAD_DATA_PREFACTURATION_CTX }
    | { type: typeof STOP_LOAD_DATA_PREFACTURATION_CTX }
    | { type: typeof START_FETCH_DATA_PREFACTURATION_CTX }
    | { type: typeof STOP_FETCH_DATA_PREFACTURATION_CTX }
    | { type: typeof UPDATE_ARTICLE_DATA_PREFACTURATION_CTX; payload: ArticlePrefacturation }
    | {
          type: typeof ADD_ARTICLE_DATA_PREFACTURATION_CTX;
          payload: { dossierId: number; article: ArticlePrefacturation };
      }
    | { type: typeof REMOVE_ARTICLE_DATA_PREFACTURATION_CTX; payload: number }
    | { type: typeof SET_BILLING_DOSSIER_IDS_CTX; payload: number[] }
    | { type: typeof START_LOAD_BILLING_DOSSIER_IDS_CTX }
    | { type: typeof STOP_LOAD_BILLING_DOSSIER_IDS_CTX }
    | { type: typeof START_FETCH_BILLING_DOSSIER_IDS_CTX }
    | { type: typeof STOP_FETCH_BILLING_DOSSIER_IDS_CTX }
    | { type: typeof SET_ACOMPTES_CTX; payload: Array<Acompte> }
    | { type: typeof ADD_ACOMPTE_CTX; payload: Acompte }
    | { type: typeof UPDATE_ACOMPTE_CTX; payload: Acompte }
    | { type: typeof REMOVE_ACOMPTE_CTX; payload: number }
    | { type: typeof START_FETCH_ACOMPTES_CTX }
    | { type: typeof STOP_FETCH_ACOMPTES_CTX }
    | { type: typeof SET_REGLEMENTS_CTX; payload: Array<Reglement> }
    | { type: typeof UPDATE_REGLEMENT_CTX; payload: Reglement }
    | { type: typeof REMOVE_REGLEMENT_CTX; payload: number }
    | { type: typeof START_FETCH_REGLEMENTS_CTX }
    | { type: typeof STOP_FETCH_REGLEMENTS_CTX }
    | { type: typeof INIT_COLLECTION_CTX; payload: Array<Collection> }
    | { type: typeof ADD_COLLECTION_CTX; payload: Collection }
    | { type: typeof UPDATE_COLLECTION_CTX; payload: Collection }
    | { type: typeof REMOVE_COLLECTION_CTX; payload: number };

// ---- Contexts ----

export const CollectionBillingContext = createContext<CollectionBillingState | null>(null);
export const CollectionBillingDispatchContext = createContext<React.Dispatch<Action> | null>(null);

// ---- Reducer ----

function collectionBillingReducer(
    state: CollectionBillingState,
    action: Action,
): CollectionBillingState {
    switch (action.type) {
        case INIT_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: { ...state.invoices, data: [...action.payload] },
            };
        case START_LOAD_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: { loaded: false, fetching: true, data: [] },
            };
        case STOP_LOAD_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: { ...state.invoices, fetching: false, loaded: true },
            };
        case START_FETCH_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: { ...state.invoices, fetching: true },
            };
        case STOP_FETCH_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: { ...state.invoices, fetching: false },
            };
        case REMOVE_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: {
                    ...state.invoices,
                    data: state.invoices.data.filter((item) => item.id !== action.payload),
                },
            };
        case UPDATE_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: {
                    ...state.invoices,
                    data: state.invoices.data.map((item) =>
                        item.id === action.payload.id ? action.payload : item,
                    ),
                },
            };
        case ADD_COLLECTION_INVOICE_CTX:
            return {
                ...state,
                invoices: {
                    ...state.invoices,
                    data: [...state.invoices.data, action.payload],
                },
            };
        case INIT_COLLECTION_ESTIMATE_CTX:
            return {
                ...state,
                estimates: { ...state.estimates, data: [...action.payload] },
            };
        case START_LOAD_COLLECTION_ESTIMATE_CTX:
            return {
                ...state,
                estimates: { loaded: false, fetching: true, data: [] },
            };
        case STOP_LOAD_COLLECTION_ESTIMATE_CTX:
            return {
                ...state,
                estimates: { ...state.estimates, fetching: false, loaded: true },
            };
        case START_FETCH_COLLECTION_ESTIMATE_CTX:
            return {
                ...state,
                estimates: { ...state.estimates, fetching: true },
            };
        case STOP_FETCH_COLLECTION_ESTIMATE_CTX:
            return {
                ...state,
                estimates: { ...state.estimates, fetching: false },
            };
        case SET_RECIPIENT_ID_CTX:
            return {
                ...state,
                recipientId: action.payload,
            };
        case SET_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: { ...state.dataPrefacturation, data: action.payload },
            };
        case START_LOAD_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: { fetching: true, loaded: false, data: null },
            };
        case STOP_LOAD_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: {
                    ...state.dataPrefacturation,
                    fetching: false,
                    loaded: true,
                },
            };
        case START_FETCH_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: { ...state.dataPrefacturation, fetching: true },
            };
        case STOP_FETCH_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: { ...state.dataPrefacturation, fetching: false },
            };
        case UPDATE_ARTICLE_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: {
                    ...state.dataPrefacturation,
                    data: {
                        ...state.dataPrefacturation.data,
                        dossiers: [
                            ...(state.dataPrefacturation.data?.dossiers || []).map((dossier) => {
                                const articles = [
                                    ...dossier.articles.filter((a) => a.id !== action.payload.id),
                                ];

                                return {
                                    ...dossier,
                                    articles: [...articles, action.payload],
                                } as DossierPrefacturation;
                            }),
                        ],
                    } as DataPrefacturation,
                },
            };
        case ADD_ARTICLE_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: {
                    ...state.dataPrefacturation,
                    data: {
                        ...state.dataPrefacturation.data,
                        dossiers: (state.dataPrefacturation.data?.dossiers || []).map((dossier) => {
                            if (dossier.id !== action.payload.dossierId) return dossier;

                            return {
                                ...dossier,
                                articles: [...dossier.articles, action.payload.article],
                            } as DossierPrefacturation;
                        }),
                    } as DataPrefacturation,
                },
            };
        case REMOVE_ARTICLE_DATA_PREFACTURATION_CTX:
            return {
                ...state,
                dataPrefacturation: {
                    ...state.dataPrefacturation,
                    data: {
                        ...state.dataPrefacturation.data,
                        dossiers: (state.dataPrefacturation.data?.dossiers || []).map(
                            (dossier) => ({
                                ...dossier,
                                articles: dossier.articles.filter((a) => a.id !== action.payload),
                            }),
                        ),
                    } as DataPrefacturation,
                },
            };
        case SET_BILLING_DOSSIER_IDS_CTX:
            return {
                ...state,
                billingDossierIds: { ...state.billingDossierIds, data: action.payload },
            };
        case START_LOAD_BILLING_DOSSIER_IDS_CTX:
            return {
                ...state,
                billingDossierIds: { fetching: true, loaded: false, data: [] },
            };
        case STOP_LOAD_BILLING_DOSSIER_IDS_CTX:
            return {
                ...state,
                billingDossierIds: {
                    ...state.billingDossierIds,
                    fetching: false,
                    loaded: true,
                },
            };
        case START_FETCH_BILLING_DOSSIER_IDS_CTX:
            return {
                ...state,
                billingDossierIds: { ...state.billingDossierIds, fetching: true },
            };
        case STOP_FETCH_BILLING_DOSSIER_IDS_CTX:
            return {
                ...state,
                billingDossierIds: { ...state.billingDossierIds, fetching: false },
            };
        case SET_ACOMPTES_CTX:
            return {
                ...state,
                acomptes: { ...state.acomptes, data: action.payload },
            };
        case ADD_ACOMPTE_CTX:
            return {
                ...state,
                acomptes: {
                    ...state.acomptes,
                    data: [...state.acomptes.data, action.payload],
                },
            };
        case UPDATE_ACOMPTE_CTX:
            return {
                ...state,
                acomptes: {
                    ...state.acomptes,
                    data: state.acomptes.data.map((item) =>
                        item.id === action.payload.id ? action.payload : item,
                    ),
                },
            };
        case REMOVE_ACOMPTE_CTX:
            return {
                ...state,
                acomptes: {
                    ...state.acomptes,
                    data: state.acomptes.data.filter((item) => item.id !== action.payload),
                },
            };
        case START_FETCH_ACOMPTES_CTX:
            return {
                ...state,
                acomptes: { loaded: false, data: [] },
            };
        case STOP_FETCH_ACOMPTES_CTX:
            return {
                ...state,
                acomptes: { ...state.acomptes, loaded: true },
            };
        case SET_REGLEMENTS_CTX:
            return {
                ...state,
                reglements: { ...state.reglements, data: action.payload },
            };
        case UPDATE_REGLEMENT_CTX:
            return {
                ...state,
                reglements: {
                    ...state.reglements,
                    data: state.reglements.data.map((item) =>
                        item.id === action.payload.id ? action.payload : item,
                    ),
                },
            };
        case REMOVE_REGLEMENT_CTX:
            return {
                ...state,
                reglements: {
                    ...state.reglements,
                    data: state.reglements.data.filter((item) => item.id !== action.payload),
                },
            };
        case START_FETCH_REGLEMENTS_CTX:
            return {
                ...state,
                reglements: { loaded: false, data: [] },
            };
        case STOP_FETCH_REGLEMENTS_CTX:
            return {
                ...state,
                reglements: { ...state.reglements, loaded: true },
            };
        case INIT_COLLECTION_CTX:
            return {
                ...state,
                collections: { ...state.collections, data: [...action.payload], loaded: true },
            };
        case ADD_COLLECTION_CTX:
            return {
                ...state,
                collections: {
                    ...state.collections,
                    data: [...state.collections.data, action.payload],
                },
            };
        case UPDATE_COLLECTION_CTX:
            return {
                ...state,
                collections: {
                    ...state.collections,
                    data: state.collections.data.map((item) =>
                        item.id === action.payload.id ? action.payload : item,
                    ),
                },
            };
        case REMOVE_COLLECTION_CTX:
            return {
                ...state,
                collections: {
                    ...state.collections,
                    data: state.collections.data.filter((item) => item.id !== action.payload),
                },
            };
        default: {
            throw new Error(`Unknown action: ${(action as Action).type}`);
        }
    }
}

// ---- Provider ----

export const CollectionBillingProvider: React.FunctionComponent<
    PropsWithChildren<DataProviderProps>
> = function ({ children }) {
    const [state, dispatch] = useReducer(collectionBillingReducer, {
        invoices: { loaded: false, fetching: false, data: [] },
        estimates: { loaded: false, fetching: false, data: [] },
        collections: { loaded: false, data: [] },
        recipientId: null,
        dataPrefacturation: { fetching: false, loaded: false, data: null },
        billingDossierIds: { fetching: false, loaded: false, data: [] },
        acomptes: { loaded: false, data: [] },
        reglements: { loaded: false, data: [] },
    });

    return (
        <CollectionBillingContext.Provider value={state}>
            <CollectionBillingDispatchContext.Provider value={dispatch}>
                {children}
            </CollectionBillingDispatchContext.Provider>
        </CollectionBillingContext.Provider>
    );
};

// ---- Hooks ----

export function useCollectionBillingCtxDispatch() {
    return useContext(CollectionBillingDispatchContext);
}

export function useCollectionBillingCtx() {
    const state = useContext(CollectionBillingContext);

    const isInvoicesLoaded = (): boolean => state?.invoices.loaded || false;
    const isFetchingInvoices = (): boolean => state?.invoices.fetching || false;
    const isEstimatesLoaded = (): boolean => state?.estimates.loaded || false;
    const isFetchingEstimates = (): boolean => state?.estimates.fetching || false;

    const getCollectionInvoices = (): Array<CollectionInvoice> => state?.invoices.data || [];

    const getCollectionInvoiceById = (id: number): CollectionInvoice | undefined =>
        (state?.invoices.data || []).find((item) => item.id === id);

    const getMultipleCollectionInvoiceFiltered = (
        filters: Partial<Record<keyof CollectionInvoice, unknown>>,
    ): Array<CollectionInvoice> =>
        (state?.invoices.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof CollectionInvoice] === value,
            ),
        );

    const getCollectionInvoiceFiltered = (
        filters: Partial<Record<keyof CollectionInvoice, unknown>>,
    ): CollectionInvoice | undefined =>
        getMultipleCollectionInvoiceFiltered(filters)[0] || undefined;

    const getCollectionEstimates = (): Array<CollectionEstimate> => state?.estimates.data || [];

    const getCollectionEstimateById = (id: number): CollectionEstimate | undefined =>
        (state?.estimates.data || []).find((item) => item.id === id);

    const getMultipleCollectionEstimateFiltered = (
        filters: Partial<Record<keyof CollectionEstimate, unknown>>,
    ): Array<CollectionEstimate> =>
        (state?.estimates.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof CollectionEstimate] === value,
            ),
        );

    const getCollectionEstimateFiltered = (
        filters: Partial<Record<keyof CollectionEstimate, unknown>>,
    ): CollectionEstimate | undefined =>
        getMultipleCollectionEstimateFiltered(filters)[0] || undefined;

    const getRecipientId = (): number | null => state?.recipientId ?? null;

    const isDataPrefacturationLoaded = (): boolean => state?.dataPrefacturation.loaded || false;

    const isFetchingDataPrefacturation = (): boolean => state?.dataPrefacturation.fetching || false;

    const getDataPrefacturation = (): DataPrefacturation | null =>
        state?.dataPrefacturation.data ?? null;

    const isBillingDossierIdsLoaded = (): boolean => state?.billingDossierIds.loaded || false;

    const isFetchingBillingDossierIds = (): boolean => state?.billingDossierIds.fetching || false;

    const getBillingDossierIds = (): number[] => state?.billingDossierIds.data ?? [];

    const isAcomptesLoaded = (): boolean => state?.acomptes.loaded || false;

    const getAcomptes = (): Array<Acompte> => state?.acomptes.data ?? [];

    const isReglementsLoaded = (): boolean => state?.reglements.loaded || false;

    const getReglements = (): Array<Reglement> => state?.reglements.data ?? [];

    const isCollectionsLoaded = (): boolean => state?.collections.loaded || false;

    const getCollections = (): Array<Collection> => state?.collections.data || [];

    const getCollectionById = (id: number): Collection | undefined =>
        (state?.collections.data || []).find((item) => item.id === id);

    const getMultipleCollectionsFiltered = (
        filters: Partial<Record<keyof Collection, unknown>>,
    ): Array<Collection> =>
        (state?.collections.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof Collection] === value,
            ),
        );

    const getCollectionFiltered = (
        filters: Partial<Record<keyof Collection, unknown>>,
    ): Collection | undefined => getMultipleCollectionsFiltered(filters)[0] || undefined;

    return {
        isInvoicesLoaded,
        isFetchingInvoices,
        isEstimatesLoaded,
        isFetchingEstimates,
        getCollectionInvoices,
        getCollectionInvoiceById,
        getMultipleCollectionInvoiceFiltered,
        getCollectionInvoiceFiltered,
        getCollectionEstimates,
        getCollectionEstimateById,
        getMultipleCollectionEstimateFiltered,
        getCollectionEstimateFiltered,
        getRecipientId,
        isDataPrefacturationLoaded,
        isFetchingDataPrefacturation,
        getDataPrefacturation,
        isBillingDossierIdsLoaded,
        isFetchingBillingDossierIds,
        getBillingDossierIds,
        isAcomptesLoaded,
        getAcomptes,
        isReglementsLoaded,
        getReglements,
        isCollectionsLoaded,
        getCollections,
        getCollectionById,
        getMultipleCollectionsFiltered,
        getCollectionFiltered,
    };
}
